import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Globe2, GraduationCap, Users, Mail, MapPin, ArrowLeft, ArrowRight } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { ClientsWall } from "../components/ClientsWall";
import { useSite, waLink, resolveMedia } from "../siteContext";
import { useLang } from "../i18n";

const ease = [0.22, 1, 0.36, 1];

const EVENT_PHOTOS = [
  { src: "/api/images/a6aafded-04bd-42e4-8d36-4ea5614bab93/file", ar: "كلمة رئيسية على المسرح", en: "Keynote on stage" },
  { src: "/api/images/3120cb14-73c5-498b-86de-3b866eedcbbd/file", ar: "برنامج قيادي لكبار التنفيذيين", en: "Executive leadership program" },
  { src: "/api/images/825b9bf5-4da2-4f63-add6-0e81254bbc2e/file", ar: "ورشة تفاعلية بأسلوب الدائرة", en: "Interactive circle workshop" },
];

const STATS = [
  { value: "+22", ar: "عاماً من الخبرة المهنية", en: "Years of professional experience" },
  { value: "+10,000", ar: "مهني تم تدريبهم", en: "Professionals trained" },
  { value: "+18", ar: "برنامجاً إلكترونياً مميزاً", en: "Signature online programs" },
  { value: "GCC+", ar: "تقديم دولي وخليجي", en: "International delivery" },
];

const EXEC_PROFILE = [
  { ar: "أكثر من 22 عاماً من الخبرة المهنية", en: "22+ years of professional experience" },
  { ar: "11 عاماً في القيادة المؤسسية الدولية (IHG Hotels & Resorts)", en: "11 years in international corporate leadership (IHG Hotels & Resorts)" },
  { ar: "11 عاماً كمدرّبة شركات وميسّرة تنفيذية ومستشارة قيادة", en: "11 years as Corporate Trainer, Executive Facilitator & Leadership Consultant" },
  { ar: "مؤسِّسة أكاديمية TheMaya", en: "Founder, TheMaya Academy" },
  { ar: "عضو هيئة تنفيذية مشاركة لمبادرات التعلم المؤسسي", en: "Associate Executive Faculty for corporate learning initiatives" },
  { ar: "برامج تُقدَّم بالعربية والإنجليزية", en: "Programs delivered in Arabic and English" },
  { ar: "مدرّبة معتمدة من TTDA (جمعية التدريب والتطوير، المملكة المتحدة)", en: "Certified Trainer through TTDA (The Training and Development Association, UK)" },
  { ar: "مدرّبة NLP وكوتش تنفيذي", en: "NLP Trainer & Executive Coach" },
  { ar: "معلمة يوغا وتأمل معتمدة", en: "Certified Yoga & Meditation Teacher" },
];

const EXPERTISE = [
  {
    ar: "تطوير القيادة", en: "Leadership Development",
    items: [
      { ar: "القيادة الواعية", en: "Conscious Leadership" },
      { ar: "قيادة الذات", en: "Self Leadership" },
      { ar: "التنوع والشمول", en: "Diversity & Inclusion" },
      { ar: "القيادة عبر التغيير والشدائد", en: "Leading Through Change & Adversity" },
      { ar: "القيادة عالية الأداء", en: "High Performance Leadership" },
      { ar: "التفكير الإبداعي والاستراتيجي", en: "Creative & Strategic Thinking" },
    ],
  },
  {
    ar: "التواصل التنفيذي", en: "Executive Communication",
    items: [
      { ar: "الخطابة العامة", en: "Public Speaking" },
      { ar: "مهارات العرض والتقديم", en: "Presentation Skills" },
      { ar: "التأثير والإقناع", en: "Influence & Persuasion" },
      { ar: "سرد القصص", en: "Storytelling" },
      { ar: "المحادثات الصعبة", en: "Difficult Conversations" },
    ],
  },
  {
    ar: "أداء الفرق", en: "Team Performance",
    items: [
      { ar: "كوتشينغ المديرين", en: "Coaching for Managers" },
      { ar: "ديناميكيات الفريق", en: "Team Dynamics" },
      { ar: "التعاون", en: "Collaboration" },
      { ar: "بناء الثقة", en: "Trust Building" },
      { ar: "حل النزاعات", en: "Conflict Resolution" },
    ],
  },
  {
    ar: "الذكاء العاطفي والأداء البشري", en: "Emotional Intelligence & Human Performance",
    items: [
      { ar: "تنظيم المشاعر", en: "Emotional Regulation" },
      { ar: "المرونة", en: "Resilience" },
      { ar: "إدارة الضغوط", en: "Stress Management" },
      { ar: "الأمان النفسي", en: "Psychological Safety" },
      { ar: "الوقاية من الاحتراق الوظيفي", en: "Burnout Prevention" },
      { ar: "الرفاه", en: "Wellbeing" },
    ],
  },
  {
    ar: "الفاعلية الشخصية", en: "Personal Effectiveness",
    items: [
      { ar: "اتخاذ القرارات", en: "Decision Making" },
      { ar: "الإنتاجية", en: "Productivity" },
      { ar: "المساءلة", en: "Accountability" },
      { ar: "عقلية النمو", en: "Growth Mindset" },
      { ar: "الثقة", en: "Confidence" },
    ],
  },
];

const PROGRAMS = [
  { ar: "القيادة الواعية™", en: "Conscious Leadership™" },
  { ar: "تنظيم المشاعر في البيئات عالية الضغط™", en: "Emotional Regulation for High-Pressure Environments™" },
  { ar: "إتقان التواصل التنفيذي™", en: "Executive Communication Mastery™" },
  { ar: "الأمان النفسي في العمل™", en: "Psychological Safety at Work™" },
  { ar: "مهارات الكوتشينغ للقادة™", en: "Coaching Skills for Leaders™" },
  { ar: "الأداء البشري والمرونة™", en: "Human Performance & Resilience™" },
  { ar: "الخطابة والحضور التنفيذي™", en: "Public Speaking & Executive Presence™" },
  { ar: "التواصل القيادي والتأثير™", en: "Leadership Communication & Influence™" },
];

const FORMATS = [
  { ar: "كلمات رئيسية تنفيذية", en: "Executive Keynotes" },
  { ar: "ورش قيادة", en: "Leadership Workshops" },
  { ar: "أكاديميات متعددة الأيام", en: "Multi-day Academies" },
  { ar: "برامج اعتماد وشهادات", en: "Certification Programs" },
  { ar: "ريتريت مؤسسية", en: "Corporate Retreats" },
  { ar: "تجارب بناء الفرق", en: "Team Building Experiences" },
  { ar: "كوتشينغ تنفيذي", en: "Executive Coaching" },
  { ar: "تدريب افتراضي مباشر", en: "Virtual Instructor-Led Training" },
  { ar: "تيسير حضوري", en: "In-Person Facilitation" },
];

const WHY = [
  { ar: "خبرة تيسير على المستوى التنفيذي", en: "Executive-level facilitation experience" },
  { ar: "منهجيات عملية مبنية على العلم", en: "Practical, science-backed methodologies" },
  { ar: "تعلم تفاعلي وتجريبي بامتياز", en: "Highly interactive and experiential learning" },
  { ar: "تفاعل قوي مع الجماهير متعددة الثقافات", en: "Strong engagement across multicultural audiences" },
  { ar: "التقديم باللغتين العربية والإنجليزية", en: "Delivery in both Arabic and English" },
  { ar: "برامج مصممة خصيصاً بما يتوافق مع أهداف أعمالكم", en: "Custom-designed programs aligned with business objectives" },
  { ar: "متاحة كعضو هيئة تنفيذية مشاركة تحت علامة الشريك", en: "Available as Associate Executive Faculty under partner branding" },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function CorporatePage() {
  const settings = useSite();
  const { lang, s } = useLang();
  const ar = lang === "ar";
  const Arrow = ar ? ArrowRight : ArrowLeft;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const waMsg = ar
    ? "مرحباً مايا، نمثّل جهة مؤسسية ونرغب بالتعاون معك في برامج قيادية"
    : "Hello Maya, we represent an organization and would like to collaborate on leadership programs";

  return (
    <div className="min-h-screen bg-luxury-black">
      <Header />
      <main className="pt-32 pb-24">
        {/* Hero */}
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <Reveal className="text-center mb-20">
            <p className="text-luxury-gold text-sm tracking-[0.35em] mb-6">{ar ? "للشركات والمؤسسات" : "FOR ORGANIZATIONS"}</p>
            <h1 data-testid="corporate-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-luxury-bone leading-[1.25] mb-4">
              Maya Taher
            </h1>
            <p className="text-luxury-gold text-base md:text-lg mb-6">
              {ar ? "عضو هيئة تنفيذية مشاركة | ميسّرة تنفيذية | أخصائية قيادة وأداء بشري" : "Associate Executive Faculty | Executive Facilitator | Leadership & Human Performance Specialist"}
            </p>
            <p className="font-serif text-2xl md:text-3xl gold-gradient-text italic mb-8">
              {ar ? "نُلهِم القادة. نُمكّن الفرق. نصنع أداءً مستداماً." : "Transforming Leaders. Empowering Teams. Driving Sustainable Performance."}
            </p>
            <p className="text-luxury-gray text-base md:text-lg leading-loose max-w-3xl mx-auto">
              {ar
                ? "مع أكثر من 22 عاماً من الخبرة المهنية — منها 11 عاماً في القيادة العليا بقطاع الضيافة الدولي و11 عاماً كمدرّبة شركات وميسّرة وكوتش وريادية — أتشارك مع المؤسسات لتطوير قادة يؤدون بوضوح ومرونة وذكاء عاطفي وهدف. تجمع منهجيتي بين علم الأعصاب وتنظيم المشاعر والقيادة الواعية والكوتشينغ وعلم النفس السلوكي والتعلم التجريبي لصناعة برامج تصنع تغييراً سلوكياً ملموساً وأثراً حقيقياً على الأعمال."
                : "With over 22 years of professional experience — including 11 years in senior leadership within the international hospitality industry and 11 years as a corporate trainer, facilitator, coach, and entrepreneur — I partner with organizations to develop leaders who perform with clarity, resilience, emotional intelligence, and purpose. My approach combines neuroscience, emotional regulation, conscious leadership, coaching, behavioral psychology, and experiential learning to create highly engaging programs that drive measurable behavioural change and business impact."}
            </p>
          </Reveal>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-24">
            {STATS.map((st, i) => (
              <Reveal key={st.en} delay={i * 0.08} className="bg-luxury-surface border border-luxury-border rounded-2xl p-6 text-center hover:border-luxury-gold transition-colors duration-500">
                <p className="font-serif text-3xl md:text-4xl gold-gradient-text mb-2">{st.value}</p>
                <p className="text-luxury-gray text-xs md:text-sm leading-relaxed">{ar ? st.ar : st.en}</p>
              </Reveal>
            ))}
          </div>

          {/* Executive profile */}
          <Reveal className="mb-24">
            <div className="flex items-center gap-4 mb-8">
              <GraduationCap className="w-6 h-6 text-luxury-gold" strokeWidth={1.25} />
              <h2 className="font-serif font-bold text-3xl md:text-5xl text-luxury-bone">
                {ar ? "الملف التنفيذي" : "Executive Profile"}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {EXEC_PROFILE.map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-luxury-surface border border-luxury-border rounded-xl px-5 py-4">
                  <Sparkles className="w-4 h-4 text-luxury-gold shrink-0 mt-1" strokeWidth={1.5} />
                  <p className="text-luxury-bone/90 text-sm md:text-base leading-relaxed">{ar ? item.ar : item.en}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Core expertise */}
          <Reveal className="mb-24">
            <div className="flex items-center gap-4 mb-8">
              <Users className="w-6 h-6 text-luxury-gold" strokeWidth={1.25} />
              <h2 className="font-serif font-bold text-3xl md:text-5xl text-luxury-bone">
                {ar ? "مجالات الخبرة الأساسية" : "Core Expertise"}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {EXPERTISE.map((cat, i) => (
                <div key={cat.en} className="bg-luxury-surface border border-luxury-border rounded-2xl p-7 hover:border-luxury-gold transition-colors duration-500">
                  <h3 className="font-serif text-xl text-luxury-gold mb-4 underline underline-offset-8 decoration-luxury-gold/40">{ar ? cat.ar : cat.en}</h3>
                  <ul className="space-y-2.5">
                    {cat.items.map((it) => (
                      <li key={it.en} className="text-luxury-gray text-sm flex items-center gap-2.5">
                        <span className="w-1 h-1 rounded-full bg-luxury-gold shrink-0" />
                        {ar ? it.ar : it.en}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Signature programs */}
          <Reveal className="mb-24">
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-luxury-bone text-center mb-10">
              {ar ? "البرامج المميزة" : "Signature Programs"}
            </h2>
            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
              {PROGRAMS.map((p) => (
                <span key={p.en} className="border border-luxury-border hover:border-luxury-gold text-luxury-bone text-sm md:text-base rounded-full px-6 py-3 transition-colors duration-400 cursor-default">
                  {ar ? p.ar : p.en}
                </span>
              ))}
            </div>
            <p className="text-center text-luxury-gray text-sm mt-8">{ar ? "(+18 برنامجاً متاحاً عند الطلب)" : "(18+ programs available upon request)"}</p>
          </Reveal>

          {/* Training formats + event photos */}
          <Reveal className="mb-24">
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-luxury-bone mb-10">
              {ar ? "أشكال التدريب" : "Training Formats"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {FORMATS.map((f) => (
                <div key={f.en} className="bg-luxury-surface border border-luxury-border rounded-xl px-5 py-4 text-luxury-bone/90 text-sm md:text-base hover:border-luxury-gold transition-colors duration-400">
                  {ar ? f.ar : f.en}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="events-gallery">
              {EVENT_PHOTOS.map((ph, i) => (
                <motion.figure
                  key={ph.src}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.1, duration: 0.8, ease }}
                  className="group relative overflow-hidden rounded-2xl border border-luxury-border hover:border-luxury-gold transition-colors duration-500"
                >
                  <img src={resolveMedia(ph.src)} alt={ar ? ph.ar : ph.en} className="w-full h-64 md:h-72 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/60 to-transparent px-5 pt-10 pb-4 text-white font-medium text-sm md:text-base [text-shadow:0_1px_10px_rgba(0,0,0,0.95)]">
                    {ar ? ph.ar : ph.en}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </Reveal>

          {/* Selected clients */}
          <Reveal className="mb-24">
            <div className="flex items-center gap-4 mb-8">
              <Globe2 className="w-6 h-6 text-luxury-gold" strokeWidth={1.25} />
              <h2 className="font-serif font-bold text-3xl md:text-5xl text-luxury-bone">
                {ar ? "نخبة من العملاء" : "Selected Clients"}
              </h2>
            </div>
            <ClientsWall />
          </Reveal>

          {/* Why organizations partner */}
          <Reveal className="mb-24">
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-luxury-bone text-center mb-10">
              {ar ? "لماذا تتشارك المؤسسات معي" : "Why Organizations Partner With Me"}
            </h2>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {WHY.map((w, i) => (
                <div key={i} className="flex items-start gap-3 border border-luxury-border rounded-xl px-5 py-4">
                  <span className="text-luxury-gold font-serif text-lg leading-none mt-0.5">✦</span>
                  <p className="text-luxury-bone/90 text-sm md:text-base">{ar ? w.ar : w.en}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Let's collaborate */}
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-luxury-gold/30 bg-luxury-surface p-10 md:p-16 text-center">
              <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[500px] h-[300px] rounded-full bg-luxury-gold/10 blur-[100px]" />
              <h2 className="font-serif font-bold text-3xl md:text-5xl text-luxury-bone mb-6 relative">
                {ar ? "لنتعاون معاً" : "Let's Collaborate"}
              </h2>
              <p className="text-luxury-gray text-base md:text-lg leading-loose max-w-2xl mx-auto mb-8 relative">
                {ar
                  ? "أتشارك مع مزودي التعليم التنفيذي والأكاديميات المؤسسية وشركات الاستشارات والجامعات وشركات التدريب الباحثة عن خبرة متميزة لتقديم برامج قيادة وتطوير مهني عالمية المستوى."
                  : "I partner with executive education providers, corporate academies, consulting firms, universities, and training companies seeking experienced faculty to deliver world-class leadership and professional development programs."}
              </p>
              <p className="font-serif text-2xl gold-gradient-text mb-2 relative">Maya Taher</p>
              <p className="text-luxury-gray text-sm mb-10 relative">
                {ar ? "مؤسِّسة | أكاديمية TheMaya" : "Founder | TheMaya Academy"}
              </p>
              <div className="flex flex-wrap justify-center items-center gap-4 relative">
                <a
                  data-testid="corporate-whatsapp-cta"
                  href={waLink(settings, waMsg, lang)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-10 py-4 rounded-full hover:bg-luxury-goldLight transition-colors duration-300 shadow-[0_10px_40px_rgba(212,175,55,0.25)]"
                >
                  {ar ? "تواصل/تواصلي عبر واتساب" : "Chat on WhatsApp"}
                </a>
                <a
                  href={`mailto:${settings.email || "Maya@themayaacademy.com"}`}
                  className="inline-flex items-center gap-2 border border-luxury-border text-luxury-bone px-8 py-4 rounded-full hover:border-luxury-gold hover:text-luxury-gold transition-colors duration-300 text-sm"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  {settings.email || "Maya@themayaacademy.com"}
                </a>
              </div>
              <p className="text-luxury-gray text-xs mt-8 flex items-center justify-center gap-2 relative">
                <MapPin className="w-3.5 h-3.5 text-luxury-gold" strokeWidth={1.5} />
                Dubai, United Arab Emirates
              </p>
            </div>
          </Reveal>

          <div className="text-center mt-16">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-luxury-gray hover:text-luxury-gold transition-colors">
              <Arrow className="w-4 h-4" strokeWidth={1.5} />
              {ar ? "العودة للرئيسية" : "Back to homepage"}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
